import React from "react";
import { Divider, Flex, Text } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickOrderDetailCard from "@/features/pathpick/features/order-history/features/detail/components/PathPickOrderDetailCard";
import PathPickOrderDetailShipmentCard from "@/features/pathpick/features/order-history/features/detail/components/shipping-section/PathPickOrderDetailShipmentCard";
import { ORDER_DETAIL_KEY } from "@/features/pathpick/features/order-history/features/detail/constants";
import {
  PathPickOrder,
  PathPickOrderShippingInfo,
} from "@/features/pathpick/features/order-history/types/response";
import {
  summarizePathPickOrderShipments,
  toPathPickOrderShipments,
} from "@/features/pathpick/features/order-history/features/detail/utils";

interface Props {
  order: PathPickOrder;
}

function PathPickOrderDetailShipping({ order }: Props) {
  const { t } = useI18nHelper();
  const shipments = toPathPickOrderShipments(order);
  const summary = summarizePathPickOrderShipments(shipments);
  // 주문 시점 배송지 스냅샷. 배송지 없이 생성된 과거 주문은 내려오지 않아 블록 자체를 접는다.
  const shippingInfo = order.shippingInfo;

  return (
    <PathPickOrderDetailCard
      title={t(`${ORDER_DETAIL_KEY}shipping_title`)}
      gap={"12px"}
      headerRight={
        <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
          {t(`${ORDER_DETAIL_KEY}shipping_summary`, summary)}
        </Text>
      }
    >
      {shippingInfo && (
        <>
          <Flex flexDirection={"column"} gap={"8px"}>
            <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
              {t(`${ORDER_DETAIL_KEY}shipping_address_title`)}
            </Text>
            <PathPickOrderDetailShippingAddressBox
              shippingInfo={shippingInfo}
            />
          </Flex>
          {/* 배송지 변경 API는 있지만 셀프 변경 UI를 두지 않는 것이 정책이라 문의 안내만 남긴다. */}
          <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
            {t(`${ORDER_DETAIL_KEY}shipping_address_notice`)}
          </Text>
          <Divider borderColor={"#EFEFED"} />
        </>
      )}

      {shipments.length === 0 ? (
        <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
          {t(`${ORDER_DETAIL_KEY}shipping_empty`)}
        </Text>
      ) : (
        <Flex flexDirection={"column"} gap={"12px"}>
          {shipments.map((shipment, index) => (
            <PathPickOrderDetailShipmentCard
              key={shipment.id}
              shipment={shipment}
              index={index + 1}
            />
          ))}
        </Flex>
      )}
    </PathPickOrderDetailCard>
  );
}

// 배송지 스냅샷 표기 — 이 카드에서만 쓰이므로 파일 안에 둔다.
// 연락처/상세주소는 값이 없을 수 있어 구분자와 함께 통째로 빠진다.
function PathPickOrderDetailShippingAddressBox({
  shippingInfo,
}: {
  shippingInfo: PathPickOrderShippingInfo;
}) {
  const recipient = [shippingInfo.recipientName, shippingInfo.contact]
    .filter(Boolean)
    .join(" · ");
  const address = [
    `(${shippingInfo.zipCode})`,
    shippingInfo.address,
    shippingInfo.detailAddress,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Flex
      flexDirection={"column"}
      gap={"6px"}
      padding={"12px 16px"}
      borderRadius={"8px"}
      background={"#F7F9FA"}
    >
      <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
        {shippingInfo.alias}
      </Text>
      <Text textStyle={"body-sm-medium"} color={"#444440"}>
        {recipient}
      </Text>
      <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
        {address}
      </Text>
    </Flex>
  );
}

export default PathPickOrderDetailShipping;
